/**
 * Used for a function to convert numbers into currency.
 * {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat NumberFormat Documentation}
 *
 * @example
 * const numberToFormat = 12345.67
 * new Intl.NumberFormat("en-US", {style: "currency"}).format(numberToFormat);
 */

/**
 * This should be the one we mainly use. It handle all cases when using the currency formatter.
 *
 * @example
 * import formatAsCurrency from "@wss/utilities/formatAsCurrency"
 *
 * const priceToFormat = 12345.67
 * const formatWithAllTheChecks = formatAsCurrency.formatIfUndefined(priceToFormat)
 *
 * return formatWithAllTheChecks;
 */
const formatAsCurrencyWrapper = (
  value: number | string | undefined,
  showCentsIfUndefined = true,
  showEmptyStringIfUndefined = false,
  numberFormatOptions: Intl.NumberFormatOptions = {},
) => {
  const formattedCents = showCentsIfUndefined ? "$0.00" : "$0";
  const undefinedFormatted = showEmptyStringIfUndefined ? "" : formattedCents;
  const parsedValue = Number(value);

  return parsedValue
    ? formatAsCurrencyPure(parsedValue, numberFormatOptions)
    : undefinedFormatted;
};

/**
 * If you want to just use a pure version of the currency formatter and handle the checks in the consumer then you can use this version.
 *
 * @example
 * import formatAsCurrency from "@wss/utilities/formatAsCurrency"
 *
 * const priceToFormat = 12345.67
 * const formatCurrencyNoChecks = formatAsCurrency.formatAsCurrencyPure(priceToFormat)
 *
 * return formatCurrencyNoChecks;
 */
const formatAsCurrencyPure = (
  value: number,
  numberFormatOptions: Intl.NumberFormatOptions = {},
) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    ...numberFormatOptions,
  }).format(value);
};

const formatAsCurrency = {
  formatIfUndefined: formatAsCurrencyWrapper,
  format: formatAsCurrencyPure,
};

export default formatAsCurrency;
